import React from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import {
  activeSchemaIdSelector,
  significanceErrorSelector,
  significanceMetricSelector,
} from '../../../../../../redux/selectors/builder';
import {
  setSignificanceFunnelReport,
  setSignificanceFunnelTimeToConvertMetric,
} from '../../../../../../redux/slices/oldBuilder';
import {
  DateKindKey,
  FunnelModeKey,
  FunnelTimeToConvertMetric,
  ISearchCohortEntityFieldsFragment,
  MetricTypeKey,
  QueryType,
} from '../../../../../../utils/apollo/graphqlSchema';
import { BuilderReportMenu } from '../../../../../../components/Builder/BuilderFields';
import { assertCondition } from '../../../../../../utils/types';
import SignificanceFunnelMetricStep from './SignificanceFunnelMetricStep';
import SignificanceFunnelTimeToConvertMetricStep from './SignificanceFunnelTimeToConvertMetricStep';

const SignificanceFunnelMetric = () => {
  const dispatch = useAppDispatch();

  const activeSchemaId = useAppSelector(activeSchemaIdSelector);
  const significanceMetric = useAppSelector(significanceMetricSelector);
  assertCondition(significanceMetric?.type === MetricTypeKey.Funnel);
  const {
    analysisId,
    steps,
    convertedOnStep,
    aliasReplacements,
    mode,
    modeSlices,
    ttcMetric,
  } = significanceMetric;
  const hasError = useAppSelector((state) =>
    significanceErrorSelector(state, 'metricAnalysis'),
  );

  const handleChangeFunnel = (
    analysis: ISearchCohortEntityFieldsFragment | null,
  ) => {
    if (!analysis) {
      dispatch(
        setSignificanceFunnelReport({
          analysisId: null,
          steps: [],
          aliasReplacements: [],
          mode: null,
          modeSlices: [],
        }),
      );
    } else {
      assertCondition(analysis.__typename === 'Funnel');
      const { id, query, pref } = analysis;

      dispatch(
        setSignificanceFunnelReport({
          analysisId: id,
          steps: query.steps,
          aliasReplacements: pref.aliasReplacements,
          mode: query.mode?.key ?? null,
          modeSlices: query.modeSlices,
        }),
      );

      if (query.mode?.key === FunnelModeKey.TimeToConvert) {
        dispatch(
          setSignificanceFunnelTimeToConvertMetric({
            ttcMetric: FunnelTimeToConvertMetric.Average,
          }),
        );
      } else {
        dispatch(
          setSignificanceFunnelTimeToConvertMetric({
            ttcMetric: null,
          }),
        );
      }
    }
  };

  return (
    <>
      <BuilderReportMenu
        activeSchemaId={activeSchemaId}
        analysisId={analysisId}
        queryType={QueryType.Funnel}
        onChange={handleChangeFunnel}
        placeholder={'Search and select Funnel'}
        hasError={hasError}
        analysisFilter={{
          expressionFilter: null,
          funnelFilter: {
            dateKinds: [DateKindKey.Event],
            modes: [FunnelModeKey.TimeToConvert, FunnelModeKey.Conversion],
          },
        }}
      />
      {analysisId && mode === FunnelModeKey.Conversion && (
        <SignificanceFunnelMetricStep
          convertedOnStep={convertedOnStep}
          steps={steps}
          aliasReplacements={aliasReplacements}
        />
      )}
      {analysisId && mode === FunnelModeKey.TimeToConvert && (
        <SignificanceFunnelTimeToConvertMetricStep
          convertedOnStep={convertedOnStep}
          selectedMetric={ttcMetric}
          modeSlices={modeSlices}
          steps={steps}
          aliasReplacements={aliasReplacements}
        />
      )}
    </>
  );
};

export default SignificanceFunnelMetric;
